import { ApolloClient } from 'apollo-client';
import { ApolloLink, from, split } from 'apollo-link';
import { WebSocketLink } from 'apollo-link-ws';
import { createUploadLink } from 'apollo-upload-client';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { getMainDefinition } from 'apollo-utilities';
import { ErrorLink } from 'apollo-link-error';

import { version } from '../package.json';
import { isAuthenticated } from 'auth';
import openNotification from 'utils/Notification';
import history from 'CustomHistory';

export const cacheData = new InMemoryCache();

// Strip __typename recursively
function stripTypeNames(obj, propToDelete) {
  for (const property in obj) {
    if (typeof obj[property] === 'object' && !(obj[property] instanceof File)) {
      const newData = stripTypeNames(obj[property], propToDelete);
      obj[property] = newData;
    } else {
      if (property === propToDelete) {
        delete obj[property];
      }
    }
  }
  return obj;
}

const removeTypenameMiddleware = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    operation.variables = stripTypeNames(operation.variables, '__typename');
  }
  return forward ? forward(operation) : null;
});

const errorLink = new ErrorLink(({ graphQLErrors, networkError, response }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      if (Array.isArray(path) && path[0] === 'currentUser') {
        history.push('/logout');
      }
      const newMessage =
        message === 'jwt expired' ? 'Please login again!' : message;
      openNotification('error', newMessage);

      if (message === 'Not Authorised!') {
        window.location.replace('/login');
      }

      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      );
    });
  }

  if (response && response.errors) {
    response.errors.forEach(({ message, locations, path }) => {
      console.log(
        `[Response error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      );
    });
  }

  if (networkError) {
    console.log(`[Network error]: ${networkError}`);
  }
});

const authMiddleware = new ApolloLink((operation, forward) => {
  const authorizationToken = isAuthenticated();
  operation.setContext({
    headers: {
      authorization: authorizationToken ? `Bearer ${authorizationToken}` : null,
      'Strict-Transport-Security': 'max-age=63072000; includeSubDomains; preload',
      'Content-Security-Policy': "frame-ancestors 'none'",
      'X-Frame-Options': 'DENY',
      'Expect-CT': `max-age=86400, enforce, report-uri="${process.env.REACT_APP_FE_DOMAIN}"`,
      'X-Content-Type-Options': 'nosniff',
      'Referrer-Policy': 'origin-when-cross-origin',
      'Cache-Control': 'no-store',
      'Clear-Site-Data': '"*"',
      'Feature-Policy': "microphone 'none'; camera 'none'",
    },
  });

  return forward(operation);
});

// Upload link with authentication
const uploadLink = createUploadLink({
  credentials: 'include',
  uri: process.env.REACT_APP_SERVER_GRAPH_URL,
});

// Combine auth + upload middleware
const httpWithUploadLink = authMiddleware.concat(uploadLink);

// Full HTTP chain with error + strip typename
const combinedHttpLink = from([
  removeTypenameMiddleware,
  errorLink,
  httpWithUploadLink,
]);

// WebSocket link for subscriptions
const wsLink = new WebSocketLink({
  uri: process.env.REACT_APP_SERVER_SOCKET_URL,
  options: {
    reconnect: true,
    timeout: 30000,
    lazy: true,
    connectionParams: async () => {
      const authorizationToken = isAuthenticated();
      return {
        authorization: authorizationToken ? `Bearer ${authorizationToken}` : null,
      };
    },
  },
});

// Clean up WebSocket connection on unload
window.addEventListener('beforeunload', () => {
  // @ts-ignore
  wsLink.subscriptionClient.close();
});

// Split: subscriptions → ws, others → http
const finalLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  combinedHttpLink
);

// Create the Apollo Client
const client = new ApolloClient({
  cache: cacheData,
  link: finalLink,
  name: 'ADMIN',
  version,
});

export default client;
